import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Checkbox, Divider, FormControlLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import { useMask } from "@react-input/mask";
import { useContext } from "react";
import { NewFormContext } from "../../../context/NewFormContext";
import { ChecklistSubitem, ChecklistType } from "../../../types/checklist/types";
import { getNumberMask, normalizeToFloat } from "../../../helpers/getNumberMask";

type props = {
  subitem: ChecklistSubitem;
  itemId: string;
  indexSubitem: number;
};

const formatCpfCnpj = (value: string): string => {
  const document = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 14);
  const isCnpj = /[A-Z]/.test(document) || document.length > 11;

  if (!isCnpj) {
    return document
      .replace(/[^\d]/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }

  const base = document.slice(0, 12);
  const checkDigits = document.slice(12).replace(/[^\d]/g, "");
  let formatted = base.slice(0, 2);

  if (base.length > 2) formatted += `.${base.slice(2, 5)}`;
  if (base.length > 5) formatted += `.${base.slice(5, 8)}`;
  if (base.length > 8) formatted += `/${base.slice(8, 12)}`;
  if (checkDigits) formatted += `-${checkDigits}`;

  return formatted;
};

export default function Generico({ subitem, itemId, indexSubitem }: props) {
  const { setModelo } = useContext(NewFormContext);

  const telefoneRef = useMask({ mask: "__ ____-_____", replacement: { _: /\d/ } });
  // const rgRef = useMask({ mask: "__.___.___-_", replacement: { _: /\d/ } });
  const placaRef = useMask({
    mask: "___-____",
    replacement: { _: /[A-Za-z0-9]/ },
  });

  const maxLenght = `${(subitem.padrao as string)?.length === undefined ? 0 : (subitem.padrao as string).length}/1000`;

  function inputRefSelect(type: string) {
    switch (type) {
      case "cpfcnpj":
        // CPF e CNPJ alfanumérico têm máscaras que dependem do próprio valor.
        // A formatação é feita em `handleValue` para permitir CNPJ iniciado por letra.
        return null;
      case "telefone":
        return telefoneRef;
      // case "rg":
      //   return rgRef;
      case "placa":
        return placaRef;
      default:
        return null;
    }
  }

  function handleValue(event: React.ChangeEvent<HTMLInputElement>) {
    if (subitem.tipo === "numero") {
      const valor = getNumberMask(event.target.value);
     if (valor === "") {
        setModelo((prev: ChecklistType) => ({
          ...prev,
          itens: prev.itens?.map((item) =>
            item.id === itemId
              ? {
                ...item,
                subitens: item.subitens.map((subitem) =>
                  subitem.id === indexSubitem
                    ? {
                      ...subitem,
                      padrao: "",
                    }
                    : subitem,
                ),
              }
              : item,
          ),
        }));
        return;
      }
      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens?.map((item) =>
          item.id === itemId
            ? {
              ...item,
              subitens: item.subitens.map((subitem) =>
                subitem.id === indexSubitem
                  ? {
                    ...subitem,
                    padrao: valor,
                  }
                  : subitem,
              ),
            }
            : item,
        ),
      }));
    } else {
      const value = subitem.tipo === "cpfcnpj"
        ? formatCpfCnpj(event.target.value)
        : event.target.value;

      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens.map((item) =>
          item.id === itemId
            ? {
              ...item,
              subitens: item.subitens.map((subitem) =>
                subitem.id === indexSubitem
                    ? {
                        ...subitem,
                        padrao: value,
                  }
                  : subitem,
              ),
            }
            : item,
        ),
      }));
    }
  }

  function handleSetType(event: React.ChangeEvent<HTMLInputElement>) {
    const checked = event.target.checked;
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) => {
              if (subitem.id === indexSubitem) {
                return {
                  ...subitem,
                  tipo: checked ? "textoLongo" : "textoCurto",
                };
              }
              return subitem;
            }),
          }
          : item,
      ),
    }));
  }

  function handleMin(value: string) {
    const valor = getNumberMask(value);
    if (valor === "") {
      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens?.map((item) =>
          item.id === itemId
            ? {
              ...item,
              subitens: item.subitens.map((subitem) =>
                subitem.id === indexSubitem
                  ? {
                    ...subitem,
                    minNumberVal: "",
                  }
                  : subitem,
              ),
            }
            : item,
        ),
      }));
      return;
    }
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === indexSubitem
                ? {
                  ...subitem,
                  minNumberVal: valor,
                }
                : subitem,
            ),
          }
          : item,
      ),
    }));
  }

  function handleMax(value: string) {
    const valor = getNumberMask(value);
    if (valor === "") {
      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens?.map((item) =>
          item.id === itemId
            ? {
              ...item,
              subitens: item.subitens.map((subitem) =>
                subitem.id === indexSubitem
                  ? {
                    ...subitem,
                    maxNumberVal: "",
                  }
                  : subitem,
              ),
            }
            : item,
        ),
      }));
      return;
    }
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === itemId
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === indexSubitem
                ? {
                  ...subitem,
                  maxNumberVal: valor,
                }
                : subitem,
            ),
          }
          : item,
      ),
    }));
  }


  const patternEmail = /^(?![.-])([A-Za-z0-9._%+-]+(?:(?<=.)[A-Za-z0-9._%+-]))@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  function CheckEmail({ valor }: { valor: string }) {
    return (
      valor?.length > 1 &&
      !patternEmail.test(valor) && (
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
          <ErrorOutlineIcon color="error" />
          <Typography fontSize={12} fontWeight={700} color="textDisabled">
            - e-mail inválido
          </Typography>
        </Stack>
      )
    );
  }

  function CheckNumber({ minNumberVal, maxNumberVal }: { minNumberVal: number | string | null; maxNumberVal: number | string | null }) {
    const min = normalizeToFloat(minNumberVal) ?? 0;
    const max = normalizeToFloat(maxNumberVal) ?? 0;
    return (
      <>
        {max < min && (
          <>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
              <ErrorOutlineIcon color="error" />
              <Typography fontSize={12} fontWeight={700} color="textDisabled">
                - Valor min não pode ser maior que max
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
              <ErrorOutlineIcon color="error" />
              <Typography fontSize={12} fontWeight={700} color="textDisabled">
                - Valor max não pode ser menor que o min
              </Typography>
            </Stack>
          </>
        )}
      </>
    );
  }

  return (
    <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" width="100%">
      <Grid container spacing={1} width="100%">
        <Grid size={4}>
          <TextField
            helperText={`${subitem.tipo === "textoLongo" ? maxLenght : ""}`}
            inputRef={inputRefSelect(subitem.tipo)}
            inputProps={{
              maxLength: `${(subitem.tipo === "cpfcnpj" && 18) || (subitem.tipo === "textoCurto" && 200) || (subitem.tipo === "textoLongo" && 1000)}`,
            }}
            fullWidth
            multiline={subitem.tipo === "textoLongo"}
            rows={subitem.tipo === "textoLongo" ? 4 : 1}
            maxRows={4}
            value={subitem.padrao ?? ""}
            onChange={handleValue}
            label="Valor padrão"
          />
        </Grid>
        <Grid size={4}>
          {(subitem.tipo === "textoCurto" || subitem.tipo === "textoLongo") && (
            <FormControlLabel
              labelPlacement="start"
              label="Texto longo"
              control={<Checkbox checked={subitem.tipo === "textoLongo"} onChange={handleSetType} />}
            />
          )}
          {subitem.tipo === "numero" && (
            <Stack direction="column" justifyContent="center" alignItems="flex-start" spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                <TextField
                  slotProps={{ htmlInput: { style: { textAlign: "center" } } }}
                  type="text"
                  value={subitem.minNumberVal ?? ""}
                  label="min"
                  variant="standard"
                  onChange={(event) => {
                    const value = getNumberMask(event.target.value);
                    handleMin(value);
                  }}
                />{" "}
                <Divider orientation="vertical" flexItem />
                <TextField
                  value={subitem.maxNumberVal ?? ""}
                  slotProps={{ htmlInput: { style: { textAlign: "center" } } }}
                  type="text"
                  label="max"
                  variant="standard"
                  onChange={(event) => {
                    const value = getNumberMask(event.target.value);
                    handleMax(value);
                  }}
                />
              </Stack>
              <CheckNumber minNumberVal={subitem.minNumberVal as number} maxNumberVal={subitem?.maxNumberVal as number} />
            </Stack>
          )}
          {subitem.tipo === "email" && <CheckEmail valor={subitem.padrao as string} />}
        </Grid>
      </Grid>
    </Stack>
  );
}
